import { Component, signal, input, OnInit, OnDestroy, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Club } from '../../../core/service/club-service/club-service';
import { AuthService, Federation } from '../../../core/service/auth/auth.service';
import { ApiService } from '../../../core/service/api-service/api-service';
import { VersionService } from '../../../core/service/version-service/version.service';
import { PermissionService } from '../../../core/service/permission/permission.service';
import { SidebarStateService } from '../../../core/service/sidebar-state/sidebar-state.service';
import { API } from '../../../core/API';

export interface MenuItem {
  label: string;
  icon: string;
  route: string;
  children?: MenuItem[];
  badgePulse?: boolean;
  badgeDot?: 'open' | 'closed';
}

@Component({
  selector: 'app-sidebar',
  standalone: false,
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  club = input<Club | null>(null);
  isOpen = signal(true);
  mobileOpen = signal(false);
  currentRoute = signal('');
  expandedItems = signal<Set<string>>(new Set());
  lastActiveListRoute = signal<string | null>(null);
  federation = signal<Federation | null>(null);
  latestCompagneName = signal<string>('Campagnes');
  logoFailedUrl = signal<string | null>(null);
  licencePeriods = signal<any[]>([]);
  version = signal<string>('');

  menuItems = signal<MenuItem[]>([]);

  private destroy$ = new Subject<void>();

  guideItems = signal<MenuItem[]>([]);

  constructor(
    private router: Router,
    private authService: AuthService,
    private api: ApiService,
    private versionService: VersionService,
    private permService: PermissionService
  ) {
    this.version.set(this.versionService.getVersion());
  }

  get isFederationView(): boolean {
    return this.authService.isFederation();
  }

  ngOnInit(): void {
    // Tablet (768–1023px): start on the icon rail so the 256px menu doesn't eat half the screen.
    if (typeof window !== 'undefined' && window.innerWidth >= 768 && window.innerWidth < 1024) {
      this.isOpen.set(false);
      this.sidebarState.isOpen.set(false);
    }
    this.federation.set(this.authService.getFederation());
    if (this.permService.can('licence', 'formulaires', 'read')) {
      this.guideItems.set([{ label: 'Formulaires', icon: 'description', route: '/Formulaires' }]);
    }
    if (this.authService.isFederation()) {
      this.loadLicencePeriods();
    } else {
      this.initMenu();
    }
    this.updateRouteState(this.router.url);
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.updateRouteState(event.urlAfterRedirects);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadLicencePeriods(): void {
    this.buildFederationMenu('closed');
    this.api.get(`${API.FEDERATION.COMPAGNES_LIST}?page=1&limit=1000`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const data = response.data || [];
          this.licencePeriods.set(data);
          this.permService.setFrozenCampaigns(data);
          const isOpen = (type: string) => data.some((p: any) => {
            const s = String(p?.licenceperiod_status_code || '').trim().toLowerCase();
            const t = String(p?.licenceperiod_type || '').trim().toUpperCase();
            return t === type && s === 'open';
          });
          const compnlStatus: 'open' | 'closed' = isOpen('COMPNL') ? 'open' : 'closed';
          this.buildFederationMenu(compnlStatus);
        },
        error: () => {
          this.buildFederationMenu('closed');
        }
      });
  }

  private buildFederationMenu(compnlStatus: 'open' | 'closed' = 'closed'): void {
    const menuItems: MenuItem[] = [
      { label: 'Tableau de bord', icon: 'dashboard', route: '/federation/dashboard' },
    ];

    menuItems.push({
      label: 'Demandes',
      icon: 'assignment',
      route: '',
      children: [
        { label: 'Joueurs', icon: 'person_pin', route: '/federation/demandes/joueurs', badgeDot: compnlStatus },
        { label: 'Transferts', icon: 'compare_arrows', route: '/federation/demandes/transferts' },
        { label: 'Dirigeants', icon: 'manage_accounts', route: '/federation/demandes/dirigeants', badgeDot: compnlStatus },
        { label: 'Staff', icon: 'groups', route: '/federation/demandes/staff', badgeDot: compnlStatus },
        { label: 'Arbitres', icon: 'sports', route: '/federation/demandes/arbitres', badgeDot: compnlStatus },
        { label: 'Clubs', icon: 'apartment', route: '/federation/demandes/clubs', badgeDot: compnlStatus },
      ],
    });

    menuItems.push({
      label: 'Licences',
      icon: 'card_membership',
      route: '',
      children: [
        { label: 'Joueurs', icon: 'people', route: '/federation/licences/joueurs' },
        { label: 'Dirigeants Club', icon: 'supervisor_account', route: '/federation/licences/dirigeants-club' },
        { label: 'Staff Club', icon: 'group', route: '/federation/licences/staff-club' },
        { label: 'Arbitres', icon: 'how_to_vote', route: '/federation/licences/arbitres-club' },
      ],
    });

    menuItems.push({ label: 'Clubs', icon: 'location_city', route: '/federation/clubs' });

    menuItems.push({ label: 'Campagnes', icon: 'event', route: '/federation/compagnes' });

    this.menuItems.set(menuItems);
    this.expandedItems.set(new Set(['Demandes']));
  }

  private initMenu(): void {
    if (this.authService.isFederation()) {
      this.buildFederationMenu();
      return;
    }

    this.buildClubMenu('closed');
    this.api.get(`${API.CLUB.COMPAGNES_LIST}?page=1&limit=1000`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const data = response.data || [];
          this.permService.setFrozenCampaigns(data);
          const isOpen = (type: string) => data.some((p: any) => {
            const s = String(p?.licenceperiod_status_code || '').trim().toLowerCase();
            const t = String(p?.licenceperiod_type || '').trim().toUpperCase();
            return t === type && s === 'open';
          });
          const compnlStatus: 'open' | 'closed' = isOpen('COMPNL') ? 'open' : 'closed';
          this.buildClubMenu(compnlStatus);
        },
        error: () => {
          this.buildClubMenu('closed');
        }
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private buildClubMenu(_compnlStatus: 'open' | 'closed' = 'closed'): void {
    // Permission-aware — not every club-scoped role has CLUB_MANAGER's full permission set
    // (e.g. CLUB_REFE only has arbitres:read, STAFF only has staff:read). An item/group only
    // appears if the account actually has read access to what it links to; a group with zero
    // visible children is dropped entirely rather than showing an empty dropdown.
    const can = (module: string) => this.permService.can('licence', module, 'read');

    const clubMenuItems: MenuItem[] = [];
    const expanded = new Set<string>();

    if (can('clubs')) {
      clubMenuItems.push({ label: 'Fiche Club', icon: 'info', route: '/club' });
    }

    const licencesChildren: MenuItem[] = [];
    if (can('joueurs')) licencesChildren.push({ label: 'Joueurs Licenciés', icon: 'people', route: '/club/licences/joueurs' });
    if (can('dirigeants')) licencesChildren.push({ label: 'Dirigeants', icon: 'supervisor_account', route: '/club/licences/dirigeants' });
    if (can('staff')) licencesChildren.push({ label: 'Staff Technique', icon: 'group', route: '/club/licences/staff' });
    if (licencesChildren.length) {
      clubMenuItems.push({ label: 'Licences', icon: 'verified_user', route: '', children: licencesChildren });
      expanded.add('Licences');
    }

    if (can('arbitres')) {
      clubMenuItems.push({ label: 'Arbitres', icon: 'sports', route: '/Arbitres' });
    }

    const nonLicenciesChildren: MenuItem[] = [];
    if (can('joueurs')) {
      nonLicenciesChildren.push({ label: 'Joueurs Non Licenciés', icon: 'people_outline', route: '/club/non-licencies/joueurs' });
      nonLicenciesChildren.push({ label: 'Joueurs Transférés', icon: 'swap_horiz', route: '/club/non-licencies/joueurs-transferes' });
    }
    if (can('dirigeants')) nonLicenciesChildren.push({ label: 'Dirigeants', icon: 'manage_accounts', route: '/club/non-licencies/dirigeants' });
    if (can('staff')) nonLicenciesChildren.push({ label: 'Staff Technique', icon: 'groups', route: '/club/non-licencies/staff' });
    if (nonLicenciesChildren.length) {
      clubMenuItems.push({ label: 'Non Licenciés', icon: 'person_off', route: '', children: nonLicenciesChildren });
      expanded.add('Non Licenciés');
    }

    // Every /club/demandes/* route is gated by the same demandes:read (not per sub-domain) —
    // one check for the whole group.
    if (this.permService.can('licence', 'demandes', 'read')) {
      clubMenuItems.push({
        label: 'Demandes', icon: 'assignment_turned_in', route: '', children: [
          { label: 'Demandes Joueurs', icon: 'person_pin', route: '/club/demandes/joueurs' },
          { label: 'Transferts Sortants', icon: 'logout', route: '/club/demandes/transferts-sortants' },
          { label: 'Transferts Entrants', icon: 'login', route: '/club/demandes/transferts-entrants' },
          { label: 'Demandes Dirigeants', icon: 'manage_accounts', route: '/club/demandes/dirigeants' },
          { label: 'Demandes Staff', icon: 'groups', route: '/club/demandes/staff' },
          { label: 'Demandes Clubs', icon: 'apartment', route: '/club/demandes/clubs' },
        ]
      });
      expanded.add('Demandes');
    }

    this.menuItems.set(clubMenuItems);
    this.expandedItems.set(expanded);
  }

  private updateRouteState(url: string): void {
    this.currentRoute.set(url);
    const childRoutes = this.menuItems()
      .flatMap(item => item.children || [])
      .map(c => c.route)
      .filter(Boolean);
    const bestChild = this.findBestMatch(url, childRoutes);
    if (bestChild) this.lastActiveListRoute.set(bestChild);
  }

  private sidebarState = inject(SidebarStateService);

  toggleSidebar(): void {
    this.isOpen.update(state => !state);
    this.sidebarState.isOpen.set(this.isOpen());
  }

  toggleMobile(): void {
    this.mobileOpen.update(state => !state);
    // The mobile drawer always shows the full menu (labels + sections), never the icon rail.
    if (this.mobileOpen() && !this.isOpen()) {
      this.isOpen.set(true);
      this.sidebarState.isOpen.set(true);
    }
  }

  closeMobile(): void {
    this.mobileOpen.set(false);
  }


  navigateTo(item: MenuItem): void {
    if (!item.route || item.route === '') {
      return;
    }

    // Close mobile sidebar when navigating
    this.closeMobile();

    // Navigate to the route
    this.router.navigate([item.route]);
  }

  toggleDropdown(label: string): void {
    // Always expand items with children
    const item = this.menuItems().find(m => m.label === label);
    if (item && item.children && item.children.length > 0) {
      this.expandedItems.update((current) => {
        const next = new Set(current);
        if (next.has(label)) next.delete(label);
        else next.add(label);
        return next;
      });
      return;
    }
    // For items without children but with non-empty route, navigate
    if (item && item.route && item.route !== '') {
      this.navigateTo(item);
    }
  }

  isActive(route: string): boolean {
    const current = this.currentRoute();
    const best = this.findBestMatch(current, this.getAllNavigableRoutes());
    return best === route;
  }

  isItemExpanded(item: MenuItem): boolean {
    if (!item.children) return false;
    const expanded = this.expandedItems().has(item.label);
    const anyChildActive = item.children.some(child => this.isChildActive(child.route));
    return expanded || anyChildActive;
  }

  isChildActive(route: string): boolean {
    const current = this.currentRoute();
    const best = this.findBestMatch(current, this.getAllNavigableRoutes());
    if (best === route) return true;

    // Only use lastActiveListRoute for unknown routes (detail pages), not for known menu routes
    const allRoutes = this.getAllNavigableRoutes();
    const isKnownRoute = allRoutes.some(r => current === r);
    if (isKnownRoute) return false;

    return this.lastActiveListRoute() === route;
  }

  isParentActive(item: MenuItem): boolean {
    if (!item.children) return false;
    return item.children.some(child => this.isChildActive(child.route));
  }

  private getAllNavigableRoutes(): string[] {
    return [
      ...this.menuItems().map(m => m.route),
      ...this.menuItems().flatMap(m => m.children?.map(c => c.route) || []),
      ...this.guideItems().map(m => m.route),
    ].filter((r): r is string => Boolean(r) && r !== '');
  }

  private findBestMatch(current: string, routes: string[]): string | null {
    let best: string | null = null;
    for (const r of routes) {
      if (current === r || current.startsWith(r + '/')) {
        if (!best || r.length > best.length) {
          best = r;
        }
      }
    }
    return best;
  }
}
